import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { closestTimeOptionIndex, filterTimeOptions, formatTime, ITimeOfDay, parseTime } from './time-input';

const TIME_OPTION_HEIGHT = 48;

/**
 * Time of day field: lists every half hour and also accepts a typed time
 * such as 1:03 PM, 13:03 or 1pm. Emits only when the time really changes.
 */
@Component({
  selector: 'app-time-field',
  templateUrl: './time-field.component.html',
  styleUrls: ['./time-field.component.scss'],
  standalone: false
})
export class TimeFieldComponent implements OnChanges {
  @Input() label = 'Time';
  @Input() ariaLabel = '';
  @Input() time: ITimeOfDay | null = null;
  @Input() disabled = false;
  /** No space reserved under the field (for list rows). */
  @Input() dense = false;

  @Output() timeChange = new EventEmitter<ITimeOfDay>();

  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;
  @ViewChild('timeOptionsPanel') autocomplete: MatAutocomplete;

  text = '';
  invalid = false;
  options = filterTimeOptions('');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['time']) {
      this.showTime();
    }
  }

  onInput(text: string): void {
    this.text = text;
    this.invalid = false;
    this.options = filterTimeOptions(text);
  }

  onBlur(): void {
    // A click on an option blurs the input first; the selection commits it.
    if (!this.trigger?.panelOpen) {
      this.commit(this.text);
    }
  }

  onOpened(): void {
    const panel = this.autocomplete?.panel?.nativeElement as HTMLElement | undefined;
    if (this.time && panel && this.options.length > 1) {
      panel.scrollTop = Math.max(0, closestTimeOptionIndex(this.time) - 2) * TIME_OPTION_HEIGHT;
    }
  }

  commit(text: string): void {
    this.options = filterTimeOptions('');
    if (!text.trim()) {
      // Cleared: keep the previous time.
      this.showTime();
      return;
    }

    const time = parseTime(text);
    if (!time) {
      this.text = text;
      this.invalid = true;
      return;
    }

    this.invalid = false;
    this.text = formatTime(time);
    if (time.hours !== this.time?.hours || time.minutes !== this.time?.minutes) {
      this.timeChange.emit(time);
    }
  }

  private showTime(): void {
    this.invalid = false;
    this.text = this.time ? formatTime(this.time) : '';
  }
}
